<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.pageSeasons.title') }}
    </portal>
    <template v-if="seasons !== null">
      <card :title="$t('admin.pageSeasons.card.ratePlanSeasons')" cols="12">
        <hm-card-inner :full-size="true">
          <hm-col cols="12">
            <draggable v-model="seasons" group="seasons">
              <hm-card-inner-line v-for="season in seasons"
                                  v-if="!season.deleted && season.ratePlanType === 'DayRate'"
                                  :title="getName(season)"
                                  :clickable="true"
                                  :key="season.id"
                                  @click="routeToSeason(season)" />
            </draggable>
          </hm-col>
        </hm-card-inner>
      </card>

      <card v-if="hasLicense('Packages')" :title="$t('admin.pageSeasons.card.packageSeasons')" cols="12">
        <hm-card-inner :full-size="true">
          <hm-col cols="12">
            <draggable v-model="seasons" group="seasons">
              <hm-card-inner-line v-for="season in seasons"
                                  v-if="!season.deleted && season.ratePlanType === 'Package'"
                                  :title="getName(season)"
                                  :clickable="true"
                                  :key="season.id"
                                  @click="routeToSeason(season)" />
            </draggable>
          </hm-col>
        </hm-card-inner>
      </card>

      <card v-if="!isEmpty(deletedSeasons)" :title="$t('admin.pageSeasons.card.deletedSeasons')" cols="12" card-type="collapsible">
        <hm-card-inner :full-size="true">
          <hm-card-inner-line v-for="season in deletedSeasons"
                              :title="getName(season)"
                              :clickable="true"
                              :key="season.id"
                              @click="routeToSeason(season)" />
        </hm-card-inner>
      </card>
    </template>

    <hm-floating-buttons id="actions" ref="floatingButtons">
      <hm-floating-button v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                          :label="`${$t('admin.pageSeasons.actions.cleanup')} 🔒`"
                          icon="delete"
                          color="error">
        <!-- delete action, 2 child buttons, super admin only, cleanup season + cleanup seasonRate -->
        <hm-floating-child-button :label="$t('admin.pageSeasons.actions.cleanupSeason')" icon="delete" color="error" @click="cleanupSeason(false)" />
        <hm-floating-child-button :label="$t('admin.pageSeasons.actions.cleanupSeasonRate')" icon="delete" color="error" @click="cleanupSeasonRate(false)" />
      </hm-floating-button>
      <hm-floating-button :label="$t('admin.pageSeasons.actions.more')" icon="plus" color="blue">
        <hm-floating-child-button :label="$t('admin.pageSeasons.actions.addRatePlanSeason')" icon="plus" color="blue" @click="createSeason('DayRate')" />
        <hm-floating-child-button v-if="hasLicense('Packages')"
                                  :label="$t('admin.pageSeasons.actions.addPackageSeason')"
                                  icon="plus"
                                  color="blue"
                                  @click="createSeason('Package')" />
      </hm-floating-button>
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import vue2Dropzone from 'vue2-dropzone';
import draggable from 'vuedraggable';
import { cloneDeep, groupBy, isEmpty, isNil, toNumber, sortBy, trim, padStart, isString } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton.vue';

export default {
  name: 'PageSeasons',

  components: {
    HmFloatingButton,
    HmCardInner,
    draggable,
  },

  autosave: [
    {
      name: 'seasons',
      variable: 'sortedSeasonIds',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        const update = {
          seasonIds: this.sortedSeasonIds,
        };

        this.axios.put(this.serviceUrls.season.sort(this, this.$route.params.seasonId), update)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
            this.$serviceBus.$emit('season.list', this.ratePlans);
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      seasons: null,
    };
  },

  computed: {
    deletedSeasons: function () {
      return isNil(this.seasons) ? null : this.seasons.filter(it => it.deleted === true);
    },
    sortedSeasonIds: function () {
      return isNil(this.seasons) ? null : this.seasons.map(it => it.id);
    },
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.season.list(this))
        .then((response) => {
          this.seasons = sortBy(response.data.data, ['sort', `name.${this.hotelSettings.details.primaryLanguage}`]);
          this.$serviceBus.$emit('season.list', this.seasons);
        });
    },
    createSeason(ratePlanType) {
      return this.axios.post(this.serviceUrls.season.list(this), {
        name: {
          [this.hotelSettings.details.primaryLanguage]: this.$t('admin.pageSeasons.newSeasonName'),
        },
        ratePlanType: ratePlanType,
        seasonType: 'Special',
        rgbColorCode: '#47b0d2',
        sort: 9999,
      })
        .then((response) => {
          this.loadData();
          this.$serviceBus.$emit('hotelSettings.update');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    getName(season) {
      const name = isNil(season) ? null : season.name;
      if (isNil(name)) return '-';
      let ret = null;

      ret = name[this.$shortLocale] || name[this.hotelSettings.details.primaryLanguage];

      let details = '';

      if (season.deleted === false) {
        details += `, ${this.$t('admin.pageSeasons.description.seasonType')}: ${this.$t(`SeasonType.${season.seasonType}`)}`;

        details += `, ${this.$t('admin.pageSeasons.description.color')}: <span style="height:1.4rem;width:1.4rem;border-radius:50%;display:inline-block;margin-left:0.25rem;transform: translateY(0.25rem);background-color:${season.rgbColorCode};opacity:0.8;"></span>`;
      }

      if (!isNil(ret)) {
        ret = ret.trim();
      }

      if (isEmpty(ret)) {
        ret = '-';
      }

      ret = `${ret}<span class="color--gray">${details}</span>`;

      if (!isEmpty(ret)) return ret;

      return name[this.hotelSettings.details.primaryLanguage];
    },
    routeToSeason(season) {
      this.$router.push({
        name: 'season',
        params: {
          seasonId: season.id,
        },
      });
    },
    cleanupSeason(confirmed) {
      if (!this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) {
        this.showErrorMessage('Access Denied');
        return;
      }
      if (confirmed === true) {
        this.axios.post(this.serviceUrls.season.cleanup(this))
          .then((response) => {
            this.showSuccessMessage();
            this.loadData();
            this.$serviceBus.$emit('hotelSettings.update');
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      } else {
        this.showConfirmModal(
          this.$t('admin.pageSeasons.confirmCleanupSeason'),
          () => {
            this.cleanupSeason(true);
          },
        );
      }
    },
    cleanupSeasonRate(confirmed) {
      if (!this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) {
        this.showErrorMessage('Access Denied');
        return;
      }
      if (confirmed === true) {
        this.axios.post(this.serviceUrls.seasonRate.cleanup(this))
          .then((response) => {
            this.showMessages(response);
            this.showSuccessMessage();
            this.loadData();
            this.$serviceBus.$emit('hotelSettings.update');
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      } else {
        this.showConfirmModal(
          this.$t('admin.pageSeasons.confirmCleanupSeasonRate'),
          () => {
            this.cleanupSeasonRate(true);
          },
        );
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageSeasons.vue